import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { fixedNumberFromInput, getTimeTextForValue } from '../../utils/utils';
import { showAlert } from './alert-service';

interface Props {
	inputType: number;
	value: number | undefined;
	onBlur: (newDisplayValue: number) => void;
	className?: string;
	minValue?: string;
	maxValue?: string;
	inputStyle?: CSSProperties;
	iconOnMax?: string;
	onChangeDebounce?: boolean;
	onClampedToMin?: () => void;
}

export default function DurationInput(props: Props) {
	const {
		inputType, // can be in seconds, minutes or seconds and minutes
		value, // value from parent component
		onBlur, // function from parent component when unfocusing input field
		className, // optional css class
		minValue, // optional minimum input value
		maxValue, // optional maximum input value
		inputStyle = {},
		iconOnMax,
		onChangeDebounce = false, // if false, component relies on blur
		onClampedToMin,
	} = props;

	const [displayValue, setDisplayValue] = useState<string>(
		getTimeTextForValue(value ?? 0, false, inputType),
	);
	const [editValue, setEditValue] = useState<string>(
		getTimeTextForValue(value ?? 0, true, inputType),
	);
	const [editingInput, setEditingInput] = useState<boolean>(false);

	const timeout = useRef<NodeJS.Timeout>();
	const clampedDuringEditRef = useRef(false);

	useEffect(() => {
		if (value !== undefined) {
			setDisplayValue(getTimeTextForValue(value, false, inputType));
			setEditValue(getTimeTextForValue(value, true, inputType));
		}
	}, [value, inputType]);

	const cleanValue = (nValue: string | number, clampToMin = true): number | null => {
		let updatedValue: string = String(nValue);
		updatedValue = updatedValue.replace('s', '');

		const parts = updatedValue.split(':');
		parts.reverse();
		if (parts.length > 1) {
			const hrs = parts[2] || 0;
			const min = parts[1];
			const sec = parts[0];
			updatedValue = ((Number(hrs) * 60 * 60) + (Number(min) * 60) + Number(sec)).toString();
		} else {
			updatedValue = Number(parts[0]).toString();
		}

		let newValue = fixedNumberFromInput(updatedValue);

		// show alert and restore original value if input is not valid
		if (Number.isNaN(Number(updatedValue))) {
			setDisplayValue(getTimeTextForValue(value ?? 0, false, inputType));
			setEditValue(getTimeTextForValue(value ?? 0, true, inputType));
			setEditingInput(false);
			showAlert({
				message: 'Please enter a valid duration',
				duration: 2000,
				type: 'warning',
			});
			return null;
		}
		if (clampToMin && minValue && newValue < Number(minValue)) {
			newValue = Number(minValue);
		}

		if (maxValue && newValue > Number(maxValue)) {
			newValue = Number(maxValue);
		}

		return newValue;
	}

	const updateComponentValues = (nValue: number) => {
		const newDisplayValue = getTimeTextForValue(nValue, false, inputType);
		const newEditValue = getTimeTextForValue(nValue, true, inputType);

		setDisplayValue(newDisplayValue);
		setEditValue(newEditValue);
	}

	const handleOnBlur = (nValue: string | number) => {
		// cancel any pending debounce so it doesn't fire after blur
		clearTimeout(timeout.current);

		// parse the raw input without min-clamping to detect if clamping occurs
		const parsedValue = cleanValue(nValue, false);
		if (parsedValue === null) {
			clampedDuringEditRef.current = false;
			return;
		}

		let newValue = parsedValue;
		if (minValue != null && newValue < Number(minValue)) {
			newValue = Number(minValue);
		} else if (maxValue != null && newValue > Number(maxValue)) {
			newValue = Number(maxValue);
		}

		// set component state
		updateComponentValues(newValue);

		setEditingInput(false);

		// notify if value was clamped to min (during editing or on this blur)
		const wasClamped = clampedDuringEditRef.current
			|| (minValue != null && parsedValue < Number(minValue));
		clampedDuringEditRef.current = false;

		if (wasClamped) {
			onClampedToMin?.();
		}

		// set external state
		onBlur(newValue);
	};

	const edit = (nValue: string | number) => {
		const cleanedValue = cleanValue(nValue, false);
		if (cleanedValue === null) return;

		// if below min, flag it but don't update the input — let the user keep typing
		if (minValue != null && cleanedValue < Number(minValue)) {
			clampedDuringEditRef.current = true;
			return;
		}

		clampedDuringEditRef.current = false;
		updateComponentValues(cleanedValue);
		onBlur(cleanedValue);
	}

	const handleEdit = (nValue: string | number) => {
		setEditValue(String(nValue));
		if (onChangeDebounce) {
			clearTimeout(timeout.current);
			timeout.current = setTimeout(() => edit(nValue), 500);
		}
	}

	useEffect(() => () => clearTimeout(timeout.current), []);

	const max = maxValue != null
		? getTimeTextForValue(Number(maxValue), false, inputType)
		: null;
	const iconAwareDisplay = (max != null && displayValue === max && iconOnMax)
		? iconOnMax
		: displayValue;

	return (
		<div className={classNames(`duration-input bl-font-p2 ${className ?? ''}`)}>
			<input
				type="text"
				className='bl-font-p2'
				value={editingInput ? editValue : iconAwareDisplay}
				onFocus={() => setEditingInput(true)}
				onBlur={(e) => handleOnBlur(e.target.value)}
				onChange={(e) => handleEdit(e.target.value)}
				style={inputStyle}
			/>
		</div>
	);
}
